// İSG uzmanı portalı — route ağacı.
//
// Auth ekranları (login / parola sıfırlama) oturum AÇIKKEN panele yönlenir
// (`RedirectIfAuthenticated`); panel oturum YOKSA login'e yönlenir (`RequireAuth`).
// Yönetim (`/admin`) alt ağacı sistem-admin sondasıyla (AdminGate) korunur — yetkisiz
// principal 403 sayfası görür; asıl yetki backend'de ([ModuleAction("admin-*")]).

import { RedirectIfAuthenticated, RequireAuth, openPersonnelState } from '@isgportal/shared';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardPage } from './pages/DashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { MobileAppPage } from './pages/MobileAppPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProfilePage } from './pages/ProfilePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminGate } from './pages/admin/AdminGate';
import { AdminShell } from './pages/admin/AdminShell';
import { AppSettingsPage } from './pages/admin/AppSettingsPage';
import { BranchDetailPage } from './pages/admin/BranchDetailPage';
import { CompaniesPage } from './pages/admin/CompaniesPage';
import { CompanyDetailPage } from './pages/admin/CompanyDetailPage';
import { KioskDevicesPage } from './pages/admin/KioskDevicesPage';
import { RolePermissionsPage } from './pages/admin/RolePermissionsPage';
import { RolesPage } from './pages/admin/RolesPage';
import { ContractorDetailPage } from './pages/contractors/ContractorDetailPage';
import { ContractorsGate } from './pages/contractors/ContractorsGate';
import { ContractorsListPage } from './pages/contractors/ContractorsListPage';
import { DepartmentReviewDetailPage } from './pages/department-review/DepartmentReviewDetailPage';
import { DepartmentReviewGate } from './pages/department-review/DepartmentReviewGate';
import { DepartmentReviewQueuePage } from './pages/department-review/DepartmentReviewQueuePage';
import { ApprovalRoutingListPage } from './pages/permit-types/ApprovalRoutingListPage';
import { ControlItemsListPage } from './pages/permit-types/ControlItemsListPage';
import { PermitTypesGate } from './pages/permit-types/PermitTypesGate';
import { PermitTypesListPage } from './pages/permit-types/PermitTypesListPage';
import { ProtectiveEquipmentListPage } from './pages/permit-types/ProtectiveEquipmentListPage';
import { PermitDetailPage } from './pages/permits/PermitDetailPage';
import { PermitsGate } from './pages/permits/PermitsGate';
import { PermitsListPage } from './pages/permits/PermitsListPage';
import { RiskAssessmentDetailPage } from './pages/risk/RiskAssessmentDetailPage';
import { RiskAssessmentEditorPage } from './pages/risk/RiskAssessmentEditorPage';
import { RiskAssessmentsGate } from './pages/risk/RiskAssessmentsGate';
import { RiskAssessmentsListPage } from './pages/risk/RiskAssessmentsListPage';
import { DocumentSchemasGate } from './pages/schemas/DocumentSchemasGate';
import { DocumentTypesListPage } from './pages/schemas/DocumentTypesListPage';
import { SecurityGate } from './pages/security/SecurityGate';
import { SettingsBranchesPage } from './pages/settings/SettingsBranchesPage';
import { SettingsCompanyPage } from './pages/settings/SettingsCompanyPage';
import { SettingsGate } from './pages/settings/SettingsGate';
import { SettingsSitesPage } from './pages/settings/SettingsSitesPage';
import { TrainingEditorPage } from './pages/trainings/TrainingEditorPage';
import { TrainingsGate } from './pages/trainings/TrainingsGate';
import { TrainingsListPage } from './pages/trainings/TrainingsListPage';
import { UsersGate } from './pages/users/UsersGate';
import { UsersPage } from './pages/users/UsersPage';
import { OnSiteVisitorsPage } from './pages/visitors/OnSiteVisitorsPage';
import { SafetyBriefingPage } from './pages/visitors/SafetyBriefingPage';
import { VisitHistoryPage } from './pages/visitors/VisitHistoryPage';
import { VisitorsGate } from './pages/visitors/VisitorsGate';

/** Eski `/contractors/:c/personnel/:u/documents` adresi → taşeron detayı, kişinin çekmecesi açık. */
function PersonnelDocumentsRedirect() {
  const { contractorUuid = '', userUuid = '' } = useParams();
  return (
    <Navigate
      to={`/contractors/${contractorUuid}`}
      replace
      state={{ openTab: 'personnel', ...openPersonnelState(userUuid) }}
    />
  );
}

export const router = createBrowserRouter([
  {
    element: (
      <RedirectIfAuthenticated redirectTo="/">
        <AuthLayout />
      </RedirectIfAuthenticated>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  {
    element: (
      <RequireAuth redirectTo="/login">
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <DashboardPage /> },
      // Kişisel profil — `profile` yetki modülü AllAuthorized (grant gerekmez); Gate YOK.
      { path: 'profile', element: <ProfilePage /> },
      // Mobil Uygulama (§26, D4) — iOS/Android indirme; `GET /app-settings` herhangi
      // authenticated okur (grant gerekmez); Gate YOK (panel oturumu yeterli).
      { path: 'mobile-app', element: <MobileAppPage /> },
      {
        // AdminGate ARTIK yalnız erişim sondasıdır (`Outlet`) — `AdminShell` kabuğu aşağıda
        // yol'suz bir iç-içe layout rotası (S33 Slice 6 owner kararı). Firma/Şube/Rol yetki
        // DETAY sayfaları kabuğun `Outlet`'i DIŞINDA, bu kapının DOĞRUDAN çocuklarıdır — kendi
        // Breadcrumb+PageHeader'larını taşırlar (permit detail ile AYNI desen); URL'ler ve
        // yetki sondası DEĞİŞMEDİ.
        path: 'admin',
        element: <AdminGate />,
        children: [
          { index: true, element: <Navigate to="/admin/roles" replace /> },
          {
            element: <AdminShell />,
            children: [
              { path: 'roles', element: <RolesPage /> },
              { path: 'companies', element: <CompaniesPage /> },
              { path: 'kiosk-devices', element: <KioskDevicesPage /> },
              // Mobil Uygulama Bağlantıları (§26) — global app-settings; yazma sistem
              // adminidir (`PUT /admin/app-settings`). AdminGate (sistem-admin sondası) altında.
              { path: 'app-settings', element: <AppSettingsPage /> },
            ],
          },
          { path: 'roles/:roleUuid', element: <RolePermissionsPage /> },
          { path: 'companies/:companyUuid', element: <CompanyDetailPage /> },
          {
            path: 'companies/:companyUuid/branches/:branchUuid',
            element: <BranchDetailPage />,
          },
        ],
      },
      {
        // Kullanıcı yönetimi (§12, tenant-scoped) — `users` gate; yetkisiz principal
        // 403 sayfası görür (asıl yetki backend'de [ModuleAction("users", …)]).
        path: 'users',
        element: <UsersGate />,
        children: [{ index: true, element: <UsersPage /> }],
      },
      {
        path: 'settings',
        element: <SettingsGate />,
        children: [
          { index: true, element: <Navigate to="/settings/company" replace /> },
          { path: 'company', element: <SettingsCompanyPage /> },
          { path: 'branches', element: <SettingsBranchesPage /> },
          { path: 'sites', element: <SettingsSitesPage /> },
        ],
      },
      {
        path: 'contractors',
        element: <ContractorsGate />,
        children: [
          { index: true, element: <ContractorsListPage /> },
          { path: ':contractorUuid', element: <ContractorDetailPage /> },
          // Eski personel belgeleri adresi (GL5: sayfa kaldırıldı) — yer imleri çalışsın diye
          // taşeron detayına yönlenir ve kişinin çekmecesini açar.
          {
            path: ':contractorUuid/personnel/:userUuid/documents',
            element: <PersonnelDocumentsRedirect />,
          },
        ],
      },
      {
        path: 'permits',
        element: <PermitsGate />,
        children: [
          { index: true, element: <PermitsListPage /> },
          { path: ':permitUuid', element: <PermitDetailPage /> },
        ],
      },
      {
        // İş İzni Tipleri (§22) + KKD kataloğu (§24) + Kontrol itemları kataloğu (§25) — hepsi
        // aynı `permit-types` gate + grant altında (D3); yetkisiz principal 403 sayfası görür (asıl
        // yetki backend'de [ModuleAction("permit-types", …)]). `PermitTypesGate` `granted`ken
        // dört çocuğu SARAN kabuğu (`PermitTypesShell`, owner review B2 item 7 — Admin/Ayarlar/
        // Ziyaretçiler kapılarıyla AYNI desen) KENDİSİ render eder; bu yüzden burada ayrı bir
        // kabuk/`Outlet` katmanı YOK, dört rota DOĞRUDAN gate'in çocuğu.
        path: 'permit-types',
        element: <PermitTypesGate />,
        children: [
          { index: true, element: <PermitTypesListPage /> },
          { path: 'protective-equipment', element: <ProtectiveEquipmentListPage /> },
          { path: 'control-items', element: <ControlItemsListPage /> },
          // Firma Onay-Routing ayarı (§31 / brief 095 §2) — Kontrol aşaması belge-türü→departman
          // yönlendirmesi; yazma yetkisi `permit-types` grant'ı (bu gate ile ortak).
          { path: 'approval-routing', element: <ApprovalRoutingListPage /> },
        ],
      },
      {
        // Kontrol aşaması (§31 / brief 095 §3) — departmanın (İSG/Hekim/İK) onayı bekleyen izinler
        // (durum `DepartmentReview`). `DepartmentReviewGate` kendi erişim sondasıyla (Kontrol kuyruğu
        // probu; gerçek modül Value `permits` + departman-rol) 403/unavailable durumlarını ele alır;
        // asıl yetki backend'de. Statik `department-review` segmenti `permits/:permitUuid` ile
        // ÇAKIŞMAZ — ayrı bir üst rota (kendi kuyruk + detay alt-ağacı).
        path: 'department-review',
        element: <DepartmentReviewGate />,
        children: [
          { index: true, element: <DepartmentReviewQueuePage /> },
          { path: ':permitUuid', element: <DepartmentReviewDetailPage /> },
        ],
      },
      {
        path: 'risk-assessments',
        element: <RiskAssessmentsGate />,
        children: [
          { index: true, element: <RiskAssessmentsListPage /> },
          // Statik `new` segmenti dinamik `:assessmentUuid`'den önce eşleşir (react-router
          // önceliği) — editör create modunda açılır.
          { path: 'new', element: <RiskAssessmentEditorPage /> },
          { path: ':assessmentUuid', element: <RiskAssessmentDetailPage /> },
        ],
      },
      {
        // Belge Şemaları (§27, #33) — `document-schemas` gate. #7'ye kadar TEK salt-okur liste;
        // tanıma ayarları doc-check'in değerlendirilmiş sürümlerinde yaşar, burada düzenlenmez.
        // Eski derin bağlantılar (`types/:uuid`, `formats/*`) kök `*` → `NotFoundPage`'e düşer.
        path: 'document-schemas',
        element: <DocumentSchemasGate />,
        children: [
          { index: true, element: <Navigate to="/document-schemas/types" replace /> },
          { path: 'types', element: <DocumentTypesListPage /> },
        ],
      },
      {
        path: 'trainings',
        element: <TrainingsGate />,
        children: [
          { index: true, element: <TrainingsListPage /> },
          // Statik `new` segmenti dinamik `:trainingUuid`'den önce eşleşir (react-router
          // önceliği) — editör create modunda açılır.
          { path: 'new', element: <TrainingEditorPage /> },
          { path: ':trainingUuid', element: <TrainingEditorPage /> },
        ],
      },
      {
        path: 'visitors',
        element: <VisitorsGate />,
        children: [
          { index: true, element: <OnSiteVisitorsPage /> },
          { path: 'history', element: <VisitHistoryPage /> },
          { path: 'safety-briefing', element: <SafetyBriefingPage /> },
        ],
      },
      // Saha Girişi (Güvenlik masası, §29, S32) — `SecurityGate` kendi içinde 403/unavailable
      // durumlarını ele alır; alt rota YOK (tek sayfa, sekmeler `SecurityDeskPage` içinde
      // istemci-taraflı state — visitors/permits gibi ayrı alt rotalar DEĞİL, tasarım spec'i §4).
      { path: 'security', element: <SecurityGate /> },
      // Bilinmeyen rota (404, owner review B2 item 5) — bu grubun SONUNCU çocuğu, `RequireAuth`
      // altında: oturum YOKSA `RequireAuth` bu dalı hiç render ETMEZ (login'e yönlenir,
      // davranış değişmedi); oturum VARSA `AppLayout` kabuğunun İÇİNDE render edilir
      // (`NotFoundPage.tsx`'in dosya başlığı notu). Eski kök `path: '*' → Navigate to="/"`
      // KALDIRILDI — bilinmeyen bir bağlantıyı sessizce Panel'e atmak yerine artık gerçek bir
      // "bulunamadı" sayfası var.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
